import { Component, OnInit } from "@angular/core";
import { AuthAccessProvider } from "../core/core.auth";
import { MessageProvider } from "../service/message.service";
import { Router } from "@angular/router";
import {
  BaseComponent,
  Document,
  FileUploadConfiguration,
  UploadModeOption,
  User,
  UserContext,
  UserInfo,
} from "../core/core.component";
import { DocumentService } from "app/service/document.service";
import { UserService } from "app/service/user.service";
import { SelectItem } from "primeng/primeng";
import { AppConstants } from "app/core/app.constants";
import { DatePipe } from "@angular/common";

declare var $: any;

@Component({
  selector: "app-documents",
  templateUrl: "./documents.component.html",
  styleUrls: ["./documents.component.css"],
  providers: [],
})
export class DocumentsComponent extends BaseComponent implements OnInit {
  it: any = AppConstants.ITA_CALENDAR_CONF;

  private userContext: UserContext = null;
  private userInfo: UserInfo = null;

  private loadingUserDocuments: boolean = false;
  private loadingSearchDocuments: boolean = false;
  private loadingUploadDocuments: boolean = false;

  // Current view selector
  private displayUserDocuments: boolean = false;
  private displayLoadDocuments: boolean = false;
  private displaySearchDocuments: boolean = false;
  // Documenti dell'utente corrente
  private userDocuments: Document[] = [];
  private filteredUserDocuments: Document[] = [];
  // Documenti dell'utente cercato (solo admin)
  private searchedDocuments: Document[] = [];
  private filteredSearchedDocuments: Document[] = [];
  // Filtri per i documenti dell'utente corrente
  private showFilterUserDocuments: boolean = false;
  private filterNameUserDocuments: string = null;
  private filterFromDateUserDocuments: Date = null;
  private filterToDateUserDocuments: Date = null;
  // Filtri per i documenti dell'utente cercato (solo admin)
  private showFilterSearchDocuments: boolean = true;
  private filterNameSearchDocuments: string = null;
  private filterFromDateSearchDocuments: Date = null;
  private filterToDateSearchDocuments: Date = null;
  // Dati per il filtro utente (solo admin)
  private rawUsersData: User[] = [];
  private filterUsers: SelectItem[] = [];
  private suggestionsUserFilter: SelectItem[] = [];
  private currentUserSelected: SelectItem = null;
  private userSelected: SelectItem = null;

  private documentToDelete: Document = null;

  // Modalità di caricamento esposte dal backend: codice, etichetta e vincoli della maschera
  private uploadModes: UploadModeOption[] = [];
  private selectedUploadMode: UploadModeOption = null;
  private loadingUploadModes: boolean = false;
  // Utenti scelti come destinatari, solo per le modalità che lo prevedono
  private uploadUsersSelected: number[] = [];

  // Campi per la schermata di caricamento documenti
  private filesToUpload: FileUploadConfiguration[] = [];
  private uploadUsersOptions: SelectItem[] = [];
  private errorMessage: string;
  private errorMessageList: string[] = [];
  private infoMessage: string;

  constructor(
    protected authAccess: AuthAccessProvider,
    protected router: Router,
    protected messageProvider: MessageProvider,
    private userService: UserService,
    protected documentService: DocumentService,
    private datePipe: DatePipe,
  ) {
    super(authAccess, router, messageProvider);
    this.messageProvider.clearAll();
  }

  setUploadMode(mode: UploadModeOption) {
    this.selectedUploadMode = mode;
    this.resetUpload(); // per pulire lo stato quando si cambia modalità
  }

  isSelectedUploadMode(mode: UploadModeOption): boolean {
    return this.selectedUploadMode != null && this.selectedUploadMode.code === mode.code;
  }

  // Vero se la modalità corrente lascia scegliere i destinatari all'operatore
  requiresUserSelection(): boolean {
    return this.selectedUploadMode != null && this.selectedUploadMode.userSelection;
  }

  // Vero se la modalità corrente accetta più file in un solo invio
  allowsMultipleFiles(): boolean {
    return this.selectedUploadMode != null && this.selectedUploadMode.multipleFiles;
  }

  // Classe bootstrap dell'avviso fisso della modalità corrente; qualsiasi tono
  // diverso da WARNING viene reso come informativo.
  getNoticeCssClass(): string {
    return this.selectedUploadMode != null &&
      this.selectedUploadMode.noticeLevel === "WARNING"
      ? "alert-warning"
      : "alert-info";
  }

  // Aggiunge o rimuove un utente dalla selezione al click sulla relativa checkbox
  toggleUserSelection(userId: number) {
    const index = this.uploadUsersSelected.indexOf(userId);
    if (index === -1) {
      this.uploadUsersSelected.push(userId);
    } else {
      this.uploadUsersSelected.splice(index, 1);
    }
  }

  // Verifica se un utente è già presente nella selezione corrente
  isUserSelected(userId: number): boolean {
    return this.uploadUsersSelected.indexOf(userId) !== -1;
  }

  // Seleziona tutti gli utenti se non lo sono già tutti, altrimenti deseleziona tutto
  toggleSelectAllUsers() {
    if (this.areAllUsersSelected()) {
      this.uploadUsersSelected = [];
    } else {
      this.uploadUsersSelected = this.uploadUsersOptions.map((u) => u.value);
    }
  }

  // Vero solo quando tutti gli utenti disponibili sono selezionati (usato anche per lo stato della checkbox "Seleziona tutti")
  areAllUsersSelected(): boolean {
    return (
      this.uploadUsersOptions.length > 0 &&
      this.uploadUsersSelected.length === this.uploadUsersOptions.length
    );
  }

  ngOnInit() {
    this.displayUserDocuments = true;
    this.userContext = this.authAccess.getUserContext();
    if (this.userContext == null) {
      this.authAccess.invalidate();
    } else {
      this.userService.currentUserInfo(this.userContext.srmToken).subscribe(
        (result) => {
          this.userInfo = result;
          this.loadingUserDocuments = true;
          this.documentService
            .getDocuments(this.userContext.srmToken)
            .subscribe((data) => {
              this.loadingUserDocuments = false;
              this.userDocuments = this.filteredUserDocuments = data.list.map(
                (raw) => new Document(raw),
              );
              this.userDocuments = this.userDocuments.sort((a, b) => {
                return (
                  new Date(b.uploadDate).getTime() -
                  new Date(a.uploadDate).getTime()
                );
              });
            });
          if (this.userInfo.admin) {
            this.userService
              .getUsers(this.userContext.srmToken, true, false)
              .subscribe((data) => {
                this.rawUsersData = data.users;
                this.rawUsersData.forEach((user) => {
                  this.uploadUsersOptions.push({
                    label: user.fullname,
                    value: user.id,
                  });
                  this.filterUsers.push({
                    label: user.fullname,
                    value: user.username,
                  });
                });
              });
            this.loadUploadModes();
          }
        },
        (error) => {
          this.handleError(error);
        },
      );
    }
  }

  // I pulsanti di scelta della modalità sono costruiti su quanto ritorna il backend:
  // qui non si fa alcuna assunzione sui codici, si viene pilotati dai flag del descrittore.
  private loadUploadModes() {
    this.loadingUploadModes = true;
    this.documentService.getDocumentUploadModes(this.userContext.srmToken).subscribe(
      (data) => {
        this.loadingUploadModes = false;
        this.uploadModes = (data.list || []).map((raw) => new UploadModeOption(raw));
        this.selectedUploadMode =
          this.uploadModes.length > 0 ? this.uploadModes[0] : null;
      },
      (error) => {
        this.loadingUploadModes = false;
        this.handleError(error);
      },
    );
  }

  display(section: string) {
    this.displayUserDocuments = false;
    this.displayLoadDocuments = false;
    this.displaySearchDocuments = false;
    switch (section) {
      case "user-documents":
        this.displayUserDocuments = true;
        break;
      case "load-documents":
        this.displayLoadDocuments = true;
        break;
      case "search-documents":
        this.displaySearchDocuments = true;
        break;
      default:
        this.displayUserDocuments = true;
        break;
    }
  }

  toggleFilter() {
    if (this.displayUserDocuments) {
      this.showFilterUserDocuments = !this.showFilterUserDocuments;
    } else if (this.displaySearchDocuments) {
      this.showFilterSearchDocuments = !this.showFilterSearchDocuments;
    }
  }

  applyUserSectionFilter() {
    this.filteredUserDocuments = this.userDocuments;
    if (this.filterNameUserDocuments) {
      this.filteredUserDocuments = this.filteredUserDocuments.filter((doc) => {
        return doc.title
          .toLowerCase()
          .includes(this.filterNameUserDocuments.toLowerCase());
      });
    }
    if (this.filterFromDateUserDocuments) {
      this.filteredUserDocuments = this.filteredUserDocuments.filter((doc) => {
        return doc.uploadDate >= this.filterFromDateUserDocuments;
      });
    }
    if (this.filterToDateUserDocuments) {
      this.filteredUserDocuments = this.filteredUserDocuments.filter((doc) => {
        return doc.uploadDate <= this.filterToDateUserDocuments;
      });
    }
  }

  applySearchSectionFilter() {
    if (this.userSelected == null) {
      this.searchedDocuments = [];
      this.filteredSearchedDocuments = [];
      this.currentUserSelected = null;
    } else if (this.userSelected != this.currentUserSelected) {
      this.searchedDocuments = [];
      this.currentUserSelected = this.userSelected;
      this.loadingSearchDocuments = true;
      this.documentService
        .getUserDocuments(
          this.userContext.srmToken,
          this.currentUserSelected.value,
        )
        .subscribe(
          (data) => {
            this.loadingSearchDocuments = false;
            this.searchedDocuments = this.filteredSearchedDocuments =
              data.list.map((raw) => new Document(raw));
            this.searchedDocuments = this.searchedDocuments.sort((a, b) => {
              return (
                new Date(b.uploadDate).getTime() -
                new Date(a.uploadDate).getTime()
              );
            });
            this.filterSearchedDocuments();
          },
          (error) => {
            this.loadingSearchDocuments = false;
            this.handleError(error);
          },
        );
    } else {
      this.filterSearchedDocuments();
    }
  }

  filterSearchedDocuments() {
    this.filteredSearchedDocuments = this.searchedDocuments;
    if (this.filterNameSearchDocuments) {
      this.filteredSearchedDocuments = this.filteredSearchedDocuments.filter(
        (doc) => {
          return doc.title
            .toLowerCase()
            .includes(this.filterNameSearchDocuments.toLowerCase());
        },
      );
    }
    if (this.filterFromDateSearchDocuments) {
      this.filteredSearchedDocuments = this.filteredSearchedDocuments.filter(
        (doc) => {
          return doc.uploadDate >= this.filterFromDateSearchDocuments;
        },
      );
    }
    if (this.filterToDateSearchDocuments) {
      this.filteredSearchedDocuments = this.filteredSearchedDocuments.filter(
        (doc) => {
          return doc.uploadDate <= this.filterToDateSearchDocuments;
        },
      );
    }
  }

  resetFilter() {
    if (this.displayUserDocuments) {
      this.showFilterUserDocuments = false;
      this.filterNameUserDocuments = null;
      this.filterFromDateUserDocuments = null;
      this.filterToDateUserDocuments = null;
      this.applyUserSectionFilter();
    } else if (this.displaySearchDocuments) {
      this.showFilterSearchDocuments = false;
      this.filterNameSearchDocuments = null;
      this.filterFromDateSearchDocuments = null;
      this.filterToDateSearchDocuments = null;
      this.applySearchSectionFilter();
    }
  }

  searchSuggestions(event) {
    this.suggestionsUserFilter = AppConstants.filterSuggestions(this.filterUsers, event.query, item => [item.label]);
  }

  viewDocument(document: Document) {
    this.documentService
      .getDocumentContent(this.userContext.srmToken, document.id)
      .subscribe((data) => {
        const blob = this.b64toBlob(data.content, data.contentType);
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        if (!document.read) {
          this.markAsRead(document);
        }
      });
  }

  private markAsRead(document: Document) {
    document.read = true;
    this.documentService
      .markDocumentAsRead(this.userContext.srmToken, document.id)
      .subscribe(
        () => {},
        (err) => {
          // tracking fallito: log e rollback del flag locale
          console.warn("Mark document as read fallito", err);
          document.read = false;
        },
      );
  }

  b64toBlob(b64Data: string, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  downloadDocument(doc: Document) {
    this.documentService
      .getDocumentContent(this.userContext.srmToken, doc.id)
      .subscribe((data) => {
        const blob = this.b64toBlob(data.content, data.contentType);
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement("a");
        document.body.appendChild(a);
        a.setAttribute("style", "display: none");
        a.href = url;
        a.download = doc.title;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        if (!doc.read) {
          this.markAsRead(doc);
        }
      });
  }

  formatDate(date: Date): string {
    return this.datePipe.transform(date, "dd-MM-yyyy HH:mm");
  }

  openFileSelector() {
    $("#fileUpload").click();
  }

  addFiles(event) {
    this.clearMessages();

    if (this.allowsMultipleFiles()) {
      for (var i = 0; i < event.target.files.length; i++) {
        let upload = new FileUploadConfiguration();
        upload.file = event.target.files[i];
        this.filesToUpload.push(upload);
      }
    } else {
      // Modalità a file singolo: il nuovo file sostituisce il precedente
      this.filesToUpload = [];
      if (event.target.files.length > 0) {
        let upload = new FileUploadConfiguration();
        upload.file = event.target.files[0];
        this.filesToUpload.push(upload);
      }
    }
    event.target.value = null;
  }

  removeDocument(index: number) {
    this.clearMessages();
    this.filesToUpload.splice(index, 1);
  }

  // Destinatari dell'invio: se la modalità non prevede la scelta sono tutti gli utenti,
  // altrimenti quelli selezionati nel menu a tendina.
  getTargetUsers(): number[] {
    if (this.selectedUploadMode == null) {
      return [];
    }
    return this.selectedUploadMode.userSelection
      ? this.uploadUsersSelected.slice()
      : this.uploadUsersOptions.map((selectItem) => selectItem.value);
  }

  // Usato dal riepilogo: evita di allocare l'array dei destinatari a ogni ciclo di change detection
  getTargetUsersCount(): number {
    if (this.selectedUploadMode == null) {
      return 0;
    }
    return this.selectedUploadMode.userSelection
      ? this.uploadUsersSelected.length
      : this.uploadUsersOptions.length;
  }

  uploadDocuments() {
    this.clearMessages();

    if (this.selectedUploadMode == null) {
      this.errorMessage = "Seleziona una modalità di caricamento.";
      return;
    }
    if (this.filesToUpload.length === 0) {
      this.errorMessage = "Nessun documento selezionato!";
      return;
    }

    // I destinatari sono risolti qui, al momento dell'invio, così coincidono
    // sempre con quanto mostrato nel riepilogo.
    const targetUsers = this.getTargetUsers();
    if (targetUsers.length === 0) {
      this.errorMessage = this.selectedUploadMode.userSelection
        ? "Seleziona almeno un utente a cui inviare i documenti."
        : "Nessun utente disponibile a cui inviare il documento.";
      return;
    }
    // Al backend si manda il codice della modalità, non l'etichetta mostrata a video
    const uploadModeCode = this.selectedUploadMode.code;

    let formData = new FormData();
    this.filesToUpload.forEach((upload) => {
      upload.targetUsers = targetUsers;
      upload.uploadMode = uploadModeCode;
      formData.append("files", upload.file);
      formData.append("uploadModes", uploadModeCode);
      formData.append("targetUserIds", targetUsers.toString());
    });

    this.loadingUploadDocuments = true;
    this.documentService
      .uploadDocuments(this.userContext.srmToken, formData)
      .subscribe(
        (response) => {
          this.loadingUploadDocuments = false;
          if (response.list != null && response.list.length > 0) {
            this.errorMessage = "";
            this.errorMessageList = response.list;
          } else {
            // La selezione utenti viene mantenuta, per poter inviare altri documenti agli stessi destinatari
            this.clearMessages();
            this.filesToUpload = [];
            this.infoMessage = "Caricamento eseguito correttamente";
          }
        },
        (error) => {
          this.loadingUploadDocuments = false;
          this.handleError(error);
        },
      );
  }

  reload() {
    if (this.displayUserDocuments) {
      this.loadingUserDocuments = true;
      this.documentService
        .getDocuments(this.userContext.srmToken)
        .subscribe((data) => {
          this.loadingUserDocuments = false;
          this.userDocuments = this.filteredUserDocuments = data.list.map(
            (raw) => new Document(raw),
          );
          this.userDocuments = this.userDocuments.sort((a, b) => {
            return (
              new Date(b.uploadDate).getTime() -
              new Date(a.uploadDate).getTime()
            );
          });
        });
    } else if (this.displaySearchDocuments && this.currentUserSelected) {
      this.loadingSearchDocuments = true;
      this.documentService
        .getUserDocuments(
          this.userContext.srmToken,
          this.currentUserSelected.value,
        )
        .subscribe(
          (data) => {
            this.loadingSearchDocuments = false;
            this.searchedDocuments = this.filteredSearchedDocuments =
              data.list.map((raw) => new Document(raw));
            this.filterSearchedDocuments();
          },
          (error) => {
            this.loadingSearchDocuments = false;
            this.handleError(error);
          },
        );
    }
  }

  showDialogDeleteDocument(doc: Document) {
    this.documentToDelete = doc;
  }

  hideDialogDeleteDocument() {
    this.documentToDelete = null;
  }

  deleteDocument() {
    this.documentService
      .deleteDocument(this.userContext.srmToken, this.documentToDelete.id)
      .subscribe(
        (data) => {
          this.documentToDelete = null;
          if (data.object) {
            this.messageProvider.notifyMessage("Documento eliminato");
            this.reload();
          } else {
            this.messageProvider.notifyMessage("Si è verificato un errore");
          }
        },
        (error) => {
          this.documentToDelete = null;
          this.handleError(error);
        },
      );
  }

  private clearMessages() {
    this.infoMessage = null;
    this.errorMessage = null;
    this.errorMessageList = [];
  }

  resetUpload() {
    this.clearMessages();
    this.filesToUpload = [];
    this.uploadUsersSelected = [];
  }
}



// WEBPACK FOOTER //
// ./src/app/documents/documents.component.ts